import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Box,
} from "@material-ui/core";
import {
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  GetApp as GetAppIcon,
} from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { exportToExcel } from "../../utils/exportToExcel";

const STANDARD_FIELDS = [
  { key: "title", label: "Título", alwaysShow: true },
  { key: "description", label: "Descrição" },
  { key: "priority", label: "Prioridade" },
  { key: "dueDate", label: "Vencimento" },
  { key: "assignedUser", label: "Responsável" },
  { key: "cliente", label: "Cliente" },
  { key: "valorReais", label: "Valor R$" },
  { key: "tags", label: "Tags" },
  { key: "tasks", label: "Tarefas" },
  { key: "createdAt", label: "Criado em" },
];

const KNOWN_CF_KEYS = new Set([
  "title",
  "description",
  "priority",
  "dueDate",
  "headerColor",
  "valorReais",
]);

const PRIORITY_LABELS = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

const PRIORITY_COLORS = {
  low: "#3498db",
  medium: "#f39c12",
  high: "#e67e22",
  urgent: "#e74c3c",
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    const date = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)
      ? new Date(`${d}T00:00:00`)
      : new Date(d);
    if (isNaN(date.getTime())) return "—";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
};

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return "—";
    return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
};

const formatCurrency = (v) => {
  if (v == null || v === "") return "—";
  try {
    return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  } catch {
    return String(v);
  }
};

const useStyles = makeStyles((theme) => ({
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(0),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(0.5),
  },
  reportTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  // Fundos claros fixos deixavam o texto branco do tema escuro ilegível;
  // na impressão o papel é branco, então volta às cores claras.
  th: {
    padding: "8px 10px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#f0f3f9",
    fontWeight: 700,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#ddd"}`,
    textAlign: "left",
    whiteSpace: "nowrap",
    "@media print": {
      backgroundColor: "#f0f3f9",
      borderColor: "#ddd",
    },
  },
  td: {
    padding: "7px 10px",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#ddd"}`,
    verticalAlign: "top",
    "@media print": {
      borderColor: "#ddd",
    },
  },
  trEven: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#fafafa",
    "@media print": {
      backgroundColor: "#fafafa",
    },
  },
  priorityBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
  },
  printOnlyHeader: {
    display: "none",
  },
}));

const getCellValue = (card, field) => {
  const title = card.customFields?.title || card.title || "—";
  const description = card.customFields?.description || card.description || null;
  const priority = card.priority || card.customFields?.priority || null;
  const dueDate = card.dueDate || card.customFields?.dueDate || null;
  const assignedUser = card.assignedUser || null;
  const cliente = card.cliente || null;
  const valorReais = card.customFields?.valorReais || null;
  const tags = card.tags || [];
  const tasks = card.tasks || [];
  const createdAt = card.createdAt || null;

  switch (field.key) {
    case "title":
      return title;
    case "description":
      return description || "—";
    case "priority":
      return priority ? PRIORITY_LABELS[priority] || priority : "—";
    case "dueDate":
      return formatDate(dueDate);
    case "assignedUser":
      return assignedUser?.name || "—";
    case "cliente":
      return cliente?.NOMEFANTASIA || cliente?.NOMECLI || cliente?.name || cliente?.nome || "—";
    case "valorReais":
      return formatCurrency(valorReais);
    case "tags":
      return Array.isArray(tags) && tags.length > 0 ? tags.join(", ") : "—";
    case "tasks": {
      if (!tasks.length) return "—";
      const done = tasks.filter((t) => t.completed).length;
      return `${done}/${tasks.length}`;
    }
    case "createdAt":
      return formatDateTime(createdAt);
    default:
      if (field.isCustom) {
        const val = card.customFields?.[field.rawKey];
        return val != null && val !== "" ? String(val) : "—";
      }
      return "—";
  }
};

const ColumnReportModal = ({ open, onClose, column, boardId }) => {
  const classes = useStyles();
  const [step, setStep] = useState("select");
  const [selectedFields, setSelectedFields] = useState([]);

  const customFieldDefs = useMemo(() => {
    const cards = column?.cards || [];
    const keys = new Set();
    cards.forEach((card) => {
      if (card.customFields && typeof card.customFields === "object") {
        Object.keys(card.customFields).forEach((k) => {
          if (!KNOWN_CF_KEYS.has(k)) keys.add(k);
        });
      }
    });
    return Array.from(keys).map((k) => ({
      key: `cf_${k}`,
      label: k,
      rawKey: k,
      isCustom: true,
    }));
  }, [column?.cards]);

  const allFields = useMemo(
    () => [...STANDARD_FIELDS, ...customFieldDefs],
    [customFieldDefs]
  );

  useEffect(() => {
    if (!open) return;
    setStep("select");
    try {
      const saved = localStorage.getItem(`kanban_report_fields_${boardId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const available = new Set(allFields.map((f) => f.key));
          setSelectedFields(parsed.filter((k) => available.has(k)));
          return;
        }
      }
    } catch {
      // ignore
    }
    setSelectedFields(
      allFields.filter((f) => !f.alwaysShow).map((f) => f.key)
    );
  }, [open, boardId, allFields]);

  const handleToggle = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleGenerate = () => {
    localStorage.setItem(
      `kanban_report_fields_${boardId}`,
      JSON.stringify(selectedFields)
    );
    setStep("report");
  };

  const handlePrint = () => {
    const style = document.createElement("style");
    style.id = "kanban-col-report-print-style";
    style.innerHTML = `
      @media print {
        body > * { visibility: hidden !important; }
        #kanban-col-report-print-area { visibility: visible !important; position: fixed; left: 0; top: 0; width: 100%; background: white; color: #000; padding: 16px; box-sizing: border-box; }
        #kanban-col-report-print-area * { visibility: visible !important; }
        #kanban-col-report-print-area .print-only-header { display: block !important; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const handleExportExcel = () => {
    const rows = cards.map((card, idx) => {
      const row = { "#": idx + 1 };
      visibleFields.forEach((field) => {
        row[field.label] = getCellValue(card, field);
      });
      return row;
    });
    exportToExcel(rows, `relatorio-${column?.name || "coluna"}`, "Cards");
  };

  const visibleFields = useMemo(() => {
    const always = STANDARD_FIELDS.filter((f) => f.alwaysShow);
    const selected = allFields.filter(
      (f) => !f.alwaysShow && selectedFields.includes(f.key)
    );
    return [...always, ...selected];
  }, [allFields, selectedFields]);

  const cards = column?.cards || [];

  if (step === "select") {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography>
          <Typography variant="h6">Selecionar Campos do Relatório</Typography>
          <Typography variant="body2" color="textSecondary">
            Coluna: <strong>{column?.name}</strong> · {cards.length} card{cards.length !== 1 ? "s" : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.sectionLabel}>Campos padrão</Typography>
          <FormGroup className={classes.fieldGrid}>
            {STANDARD_FIELDS.filter((f) => !f.alwaysShow).map((field) => (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    checked={selectedFields.includes(field.key)}
                    onChange={() => handleToggle(field.key)}
                    color="primary"
                    size="small"
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>
          {customFieldDefs.length > 0 && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Typography className={classes.sectionLabel}>Campos personalizados</Typography>
              <FormGroup className={classes.fieldGrid}>
                {customFieldDefs.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.key)}
                        onChange={() => handleToggle(field.key)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleGenerate} variant="contained" color="primary">
            Gerar Relatório
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { maxHeight: "90vh" } }}
    >
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Tooltip title="Voltar à seleção de campos">
              <IconButton size="small" onClick={() => setStep("select")} style={{ marginRight: 8 }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6">Relatório — {column?.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {cards.length} card{cards.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Tooltip title="Exportar Excel">
              <IconButton onClick={handleExportExcel}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ padding: 0 }}>
        <div id="kanban-col-report-print-area" style={{ padding: 16 }}>
          <div className={`print-only-header ${classes.printOnlyHeader}`}>
            <h2 style={{ margin: 0, fontSize: 16 }}>Relatório — {column?.name}</h2>
            <p style={{ margin: "4px 0 12px 0", fontSize: 12 }}>
              Gerado em: {formatDateTime(new Date().toISOString())} · {cards.length} card{cards.length !== 1 ? "s" : ""}
            </p>
          </div>
          {cards.length === 0 ? (
            <Typography color="textSecondary" align="center" style={{ padding: 24 }}>
              Nenhum card nesta coluna.
            </Typography>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className={classes.reportTable}>
                <thead>
                  <tr>
                    <th className={classes.th} style={{ width: 36, textAlign: "center" }}>#</th>
                    {visibleFields.map((f) => (
                      <th key={f.key} className={classes.th}>
                        {f.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card, idx) => (
                    <tr key={card.id} className={idx % 2 === 1 ? classes.trEven : ""}>
                      <td className={classes.td} style={{ textAlign: "center", color: "#aaa", fontSize: 11 }}>
                        {idx + 1}
                      </td>
                      {visibleFields.map((field) => (
                        <td key={field.key} className={classes.td}>
                          {field.key === "priority" &&
                          (card.priority || card.customFields?.priority) ? (
                            <span
                              className={classes.priorityBadge}
                              style={{
                                backgroundColor:
                                  PRIORITY_COLORS[
                                    card.priority || card.customFields?.priority
                                  ] || "#aaa",
                              }}
                            >
                              {getCellValue(card, field)}
                            </span>
                          ) : (
                            getCellValue(card, field)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExportExcel}>
          Exportar Excel
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnReportModal;
